"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import Sketch from "react-p5";
import p5Types from "p5";
const SHARED_DATA = {
  business_name: "MollyCall.ai",
  site_url: "https://mollycall.ai"
};
const MagneticButton = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!buttonRef.current) return;
    const {
      left,
      top,
      width,
      height
    } = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - (left + width / 2);
    const y = e.clientY - (top + height / 2);
    setPosition({
      x: x * 0.2,
      y: y * 0.2
    });
  };
  const handleMouseLeave = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  return <div data-section-id="21645" ref={buttonRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className={className} style={{
    transform: `translate(${position.x}px, ${position.y}px)`,
    transition: "transform 0.1s ease-out"
  }}>
      {children}
    </div>;
};
const FontCycler = () => {
  const fonts = ["font-sans", "font-mono", "font-sans font-black", "italic font-sans", "font-bold font-mono"];
  const words = ["Voice", "Calls", "Growth", "Leads", "Service"];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % words.length);
    }, 800);
    return () => clearInterval(interval);
  }, [words.length]);
  return <span className={`inline-block transition-all duration-300 text-primary ${fonts[index % fonts.length]}`}>
      {words[index % words.length]}
    </span>;
};
const PortfolioMorphSketch = () => {
  const MODE_PHOTO = 0;
  const MODE_INDUSTRIAL = 1;
  const MODE_UIUX = 2;
  const [mode, setMode] = useState(MODE_PHOTO);
  const modeRef = useRef(MODE_PHOTO);
  useEffect(() => {
    const interval = setInterval(() => {
      setMode(prev => {
        const next = (prev + 1) % 3;
        modeRef.current = next;
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const numParticles = 12;
  const particles = useRef<any[]>([]);
  const setup = (p5: p5Types, canvasParentRef: Element) => {
    p5.createCanvas(p5.windowWidth > 800 ? 600 : p5.windowWidth - 40, 500, p5.WEBGL).parent(canvasParentRef);
    for (let i = 0; i < numParticles; i++) {
      particles.current.push({
        x: 0,
        y: 0,
        z: 0,
        tx: 0,
        ty: 0,
        tz: 0,
        w: 0,
        h: 0,
        d: 0,
        tw: 0,
        th: 0,
        td: 0,
        color: p5.color(255)
      });
    }
  };
  const draw = (p5: p5Types) => {
    p5.background(18, 18, 20);
    p5.orbitControl();
    p5.ambientLight(60);
    p5.pointLight(255, 255, 255, 200, -200, 200);
    p5.directionalLight(55, 120, 255, -1, 0.5, -1);
    const m = modeRef.current;
    const t = p5.millis() * 0.001;
    particles.current.forEach((p, i) => {
      if (m === MODE_PHOTO) {
        const col = i % 4;
        const row = Math.floor(i / 4);
        p.tx = (col - 1.5) * 90;
        p.ty = (row - 1) * 110;
        p.tz = Math.sin(t + i) * 20;
        p.tw = 80;
        p.th = 100;
        p.td = 5;
      } else if (m === MODE_INDUSTRIAL) {
        const angle = i / numParticles * p5.TWO_PI;
        const radius = 120;
        p.tx = Math.cos(angle + t) * radius;
        p.ty = Math.sin(angle + t) * radius * 0.5;
        p.tz = Math.sin(angle * 2 + t) * 50;
        p.tw = 40;
        p.th = 40;
        p.td = 40;
      } else {
        if (i === 0) {
          p.tx = 0;
          p.ty = -120;
          p.tz = 0;
          p.tw = 300;
          p.th = 40;
          p.td = 10;
        } else if (i < 4) {
          p.tx = -110;
          p.ty = -60 + (i - 1) * 60;
          p.tz = 0;
          p.tw = 60;
          p.th = 50;
          p.td = 10;
        } else {
          const idx = i - 4;
          const col = idx % 2;
          const row = Math.floor(idx / 2);
          p.tx = 20 + col * 110;
          p.ty = -60 + row * 80;
          p.tz = 10;
          p.tw = 100;
          p.th = 70;
          p.td = 5;
        }
      }
      p.x = p5.lerp(p.x, p.tx, 0.05);
      p.y = p5.lerp(p.y, p.ty, 0.05);
      p.z = p5.lerp(p.z, p.tz, 0.05);
      p.w = p5.lerp(p.w, p.tw, 0.05);
      p.h = p5.lerp(p.h, p.th, 0.05);
      p.d = p5.lerp(p.d, p.td, 0.05);
      p5.push();
      p5.translate(p.x, p.y, p.z);
      p5.noStroke();
      const r = p5.map(p.x, -150, 150, 50, 100);
      const g = p5.map(p.y, -150, 150, 100, 200);
      const b = 255;
      p5.fill(r, g, b, 200);
      p5.specularMaterial(250);
      p5.shininess(80);
      p5.box(p.w, p.h, p.d);
      p5.pop();
    });
    p5.rotateY(t * 0.2);
  };
  const windowResized = (p5: p5Types) => {
    p5.resizeCanvas(p5.windowWidth > 800 ? 600 : p5.windowWidth - 40, 500);
  };
  return <div className="relative w-full h-[500px] flex items-center justify-center overflow-hidden rounded-[10px] border border-white/10 bg-black/40 backdrop-blur-md shadow-2xl">
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <span className={`text-xs px-2 py-1 rounded-full border ${mode === MODE_PHOTO ? 'bg-primary text-white border-primary' : 'text-muted-foreground border-white/10'}`}>Inbound</span>
        <span className={`text-xs px-2 py-1 rounded-full border ${mode === MODE_INDUSTRIAL ? 'bg-primary text-white border-primary' : 'text-muted-foreground border-white/10'}`}>Outbound</span>
        <span className={`text-xs px-2 py-1 rounded-full border ${mode === MODE_UIUX ? 'bg-primary text-white border-primary' : 'text-muted-foreground border-white/10'}`}>Intelligence</span>
      </div>
      <Sketch setup={setup} draw={draw} windowResized={windowResized} />
    </div>;
};
export default function ManifestYourVision() {
  return <section id="the-future-of-voice" className="dark bg-background text-foreground relative w-full min-h-screen flex flex-col justify-center overflow-hidden py-20 lg:py-0">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse delay-1000" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {}
          <div className="flex flex-col items-start space-y-10">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-primary/90 font-mono text-sm tracking-widest uppercase mb-4">
                <Sparkles className="w-4 h-4" />
                <span>Enterprise Voice AI</span>
              </div>
              
              <h1 className="text-6xl md:text-8xl lg:text-[110px] leading-[0.9] font-sans font-black tracking-tighter">
                The Future of <br />
                <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">AI</span> <FontCycler />
              </h1>
              
              <p className="text-[22px] text-muted-foreground max-w-md mt-6 font-sans font-light leading-relaxed">
                Automate your business phone calls with intelligent AI agents. 
                Experience human-like conversations that drive conversions 24/7.
              </p>
            </div>

            <MagneticButton className="inline-block">
              <Link to="/ContactUsPage">
                <Button size="lg" className="h-20 px-12 text-xl rounded-[10px] bg-primary hover:bg-primary/90 text-white shadow-[0_0_50px_-10px_rgba(55,120,255,0.5)] transition-all duration-300 hover:scale-105 group relative overflow-hidden">
                  <span className="relative z-10">Request Demo</span>
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite] transition-transform" />
                  <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform relative z-10" />
                </Button>
              </Link>
            </MagneticButton>

            <div className="flex items-center gap-12 pt-8 border-t border-white/10 w-full max-w-md">
              <div>
                <p className="text-3xl font-bold font-mono text-primary">0.2s</p>
                <p className="text-sm text-muted-foreground uppercase tracking-widest font-medium">Latency</p>
              </div>
              <div>
                <p className="text-3xl font-bold font-mono text-primary">99%</p>
                <p className="text-sm text-muted-foreground uppercase tracking-widest font-medium">Accuracy</p>
              </div>
            </div>
          </div>

          {}
          <div className="w-full h-full flex items-center justify-center lg:justify-end perspective-1000">
            <div className="w-full max-w-[600px] transform transition-transform duration-700 hover:scale-[1.02]">
              <PortfolioMorphSketch />
            </div>
          </div>

        </div>
      </div>
    </section>;
}